import { Injectable } from "@angular/core";
import { AuthenticationService } from "./auth.service";
import { AttributeModel } from "../main/dynamic/model/attribute.model";
import { map } from "rxjs/operators";
import { Observable } from "rxjs";
import { MotorPolicyService } from "./motor-policy.service";
import { ProcessModel } from "../model/process.model";
import { PolicyService } from "./policy.service";
import { PolicyModel } from "../model/policy.model";
import { Router } from "@angular/router";

@Injectable()
export class DesignService {
    
    constructor(
        private motorPolicyService: MotorPolicyService,
        private authService: AuthenticationService,
        private policyService: PolicyService,
        private router: Router) {
    }

    getFromDefault(key: string): AttributeModel {
        let defaultAttr = this.getSettings("default")

        if(defaultAttr) {
            return defaultAttr.attributes
                .find(attr => attr.key.toLowerCase() == key.toLowerCase())
        }
    }

    getMainMenu(): AttributeModel[] {
        let backofficeAttrs = this.getSettings("backoffice")

        if(backofficeAttrs) {
            let menuAttr = backofficeAttrs.attributes.find(attr => 
                attr.type.toLowerCase() == "MainMenu".toLowerCase())

            if(menuAttr) {
                return menuAttr.attributes
                    .filter(attr => this.hasPermission(attr))
            }
        }

        return []
    }

    getCurrentMenuConfig(): AttributeModel {
        const menuAttrs = this.getMainMenu();

        const currentRoute = this.router.url.toLowerCase();

        const matchingMenuAttr = menuAttrs.find(attr => {
            const attrValue = attr.value.toLowerCase();
            return currentRoute.includes(attrValue);
        });

        return matchingMenuAttr || null;
    }

    getMenuNameFromRoute(route: string): string {
        if(route) {
            let foundRouteAttr = this.getMainMenu().find(attr => 
                attr.value.toLowerCase() == route.toLowerCase())

            if(foundRouteAttr) {
                return foundRouteAttr.key
            }
        }

        return null
    }

    getNewRegisterMenuItems(): AttributeModel[] {
        let backofficeAttrs = this.getSettings("backoffice");
    
        if (backofficeAttrs) {
            return backofficeAttrs.attributes.filter(attr => 
                attr.type.toLowerCase() === "newregister" && this.hasPermission(attr)
            );
        }
    
        return [];
    }

    private hasPermission(item: AttributeModel): boolean {
        let restriction = item.attributes.find(attr => 
            attr.key.toLowerCase() == "user" && attr.type.toLowerCase() == "restriction")

        if(restriction) {
            let roles = restriction.attributes
                .filter(attr => attr.key.toLowerCase() == "role")
                .map(attr => attr.value)

            if(roles.length == 0) {
                return true;
            }

            let currentUser = this.authService.getUser()

            if(currentUser && currentUser.roles) {
                return roles.some(role => currentUser.roles.includes(role))
            } else {
                return false;
            }
        }

        return true;
    }

    getPageTitle(): string {
        let formSettings = this.getSettings("forms")

        if(formSettings) {
            let titleAttr = formSettings.attributes
                .find(attr => attr.key.toLowerCase() == "title")

            if(titleAttr) {
                return titleAttr.value
            }
        }

        return ""
    }

    getSettings(key: string): AttributeModel {
        let storageAttrs = localStorage.getItem("designAttributes")

        if(storageAttrs) {
            let attributes = <AttributeModel[]>JSON.parse(storageAttrs)

            return attributes.find(attr => attr.key.toLowerCase() == key.toLowerCase())
        }
    }

    getStartMenu(): AttributeModel {
        let backofficeAttrs = this.getSettings("backoffice")

        if(backofficeAttrs) {
            let startAttr = backofficeAttrs.attributes.find(attr => 
                attr.key.toLowerCase() == "start")

            if(startAttr) {
                return startAttr;
            }
        }

        return null
    }

    setColors() {
        let colorSettings = this.getFromDefault("ColorSettings")

        let primary = "#5776df"
        let secondary = "#111148"
        let tertiary = "#212121"
        let font_btn_primary = "#ffffff"
        let font_btn_secondary = "#ffffff"
        let header_bg = "#ffffff"
        let side_menu_bg: string = null
        let main_bg: string = null
        let btn_primary_bg: string = null
        let btn_secondary_bg: string = null

        if(colorSettings) {
            primary = this.getFromColorSettings(colorSettings, "primary");
            secondary = this.getFromColorSettings(colorSettings, "secondary");
            tertiary = this.getFromColorSettings(colorSettings, "tertiary");
            font_btn_primary = this.getFromColorSettings(colorSettings, "font_btn_primary");
            font_btn_secondary = this.getFromColorSettings(colorSettings, "font_btn_secondary");
            header_bg = this.getFromColorSettings(colorSettings, "header_bg") || header_bg;
            side_menu_bg = this.getFromColorSettings(colorSettings, "side_menu_bg") || side_menu_bg;
            btn_primary_bg = this.getFromColorSettings(colorSettings, "btn_primary_bg") || btn_primary_bg;
            btn_secondary_bg = this.getFromColorSettings(colorSettings, "btn_secondary_bg") || btn_secondary_bg;
        }

        // main_bg_color pode vir de default (preferencial) ou de ColorSettings
        const mainBgAttr = this.getFromDefault("main_bg_color");
        if (mainBgAttr && mainBgAttr.value) {
            main_bg = (mainBgAttr.value || "").toString().trim();
        } else if (colorSettings) {
            const mainFromSettings = this.getFromColorSettings(colorSettings, "main_bg_color");
            if (mainFromSettings) {
                main_bg = (mainFromSettings || "").toString().trim();
            }
        }

        document.documentElement.style.setProperty('--primary-color', primary);
        document.documentElement.style.setProperty('--secondary-color', secondary);
        document.documentElement.style.setProperty('--tertiary-color', tertiary);
        document.documentElement.style.setProperty('--font-btn-primary-color', font_btn_primary);
        document.documentElement.style.setProperty('--font-btn-secondary-color', font_btn_secondary);
        document.documentElement.style.setProperty('--header-bg-color', header_bg);

        // side_menu_bg: usa a variável apenas se configurada; caso contrário, remove para cair no fallback (primary)
        if (side_menu_bg) {
            document.documentElement.style.setProperty('--side-menu-bg-color', side_menu_bg);
        } else {
            document.documentElement.style.removeProperty('--side-menu-bg-color');
        }

        // main bg: define variável apenas se presente; caso contrário, remove para manter cor atual
        if (main_bg) {
            document.documentElement.style.setProperty('--main-bg-color', main_bg);
        } else {
            document.documentElement.style.removeProperty('--main-bg-color');
        }

        // botões: aplica variáveis se configuradas; senão, remove para manter fallback
        if (btn_primary_bg) {
            document.documentElement.style.setProperty('--btn-primary-bg-color', btn_primary_bg);
        } else {
            document.documentElement.style.removeProperty('--btn-primary-bg-color');
        }

        if (btn_secondary_bg) {
            document.documentElement.style.setProperty('--btn-secondary-bg-color', btn_secondary_bg);
        } else {
            document.documentElement.style.removeProperty('--btn-secondary-bg-color');
        }
    }

    setFonts() {
        let fontFamily = "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";

        const backoffice = this.getSettings("backoffice");
        if (backoffice) {
            const fontAttr = backoffice.attributes
                .find(attr => (attr.key || "").toLowerCase() === "fontfamily");
            if (fontAttr && fontAttr.value) {
                fontFamily = fontAttr.value;
            }
        }

        try {
            document.documentElement.style.setProperty('--app-font-family', fontFamily);
            if (document && document.body) {
                document.body.style.fontFamily = fontFamily;
            }
        } catch {}
    }

    setFontSize() {
        const rootComputed = getComputedStyle(document.documentElement);
        const bodyComputed = getComputedStyle(document.body);
        const rootPx = parseFloat(rootComputed.fontSize) || 16;
        const currentPx = parseFloat(bodyComputed.fontSize) || 16;
        const maxPx = 20;
        let requestedPx: number = null;

        const backoffice = this.getSettings("backoffice");

        if (backoffice) {
            const sizeAttr = backoffice.attributes
                .find(attr => (attr.key || "").toLowerCase() === "fontsize");

            if (sizeAttr && sizeAttr.value) {
                const raw = (sizeAttr.value || "").toString().trim().toLowerCase();
                if (/^\d+(?:\.\d+)?px$/.test(raw)) {
                    requestedPx = parseFloat(raw.replace("px", ""));
                } else if (/^\d+(?:\.\d+)?$/.test(raw)) {
                    requestedPx = parseFloat(raw);
                } else if (/^\d+(?:\.\d+)?rem$/.test(raw)) {
                    const rem = parseFloat(raw.replace("rem", ""));
                    requestedPx = rem * rootPx;
                }
            }
        }

        let finalPx = currentPx;
        if (typeof requestedPx === 'number' && !isNaN(requestedPx)) {
            if (currentPx <= maxPx) {
                finalPx = Math.max(currentPx, Math.min(maxPx, requestedPx));
            } else {
                finalPx = currentPx;
            }
        }

        const finalRem = (finalPx / rootPx) + "rem";

        try {
            document.documentElement.style.setProperty('--app-font-size-base', finalRem);
            if (document && document.body) {
                document.body.style.fontSize = finalRem;
            }
        } catch {}
    }

    private getFromColorSettings(colorSettings: AttributeModel, key: string): string {
        let settingsAttr = colorSettings.attributes
            .find(attr => attr.key.toLowerCase() == key.toLowerCase());

        if (settingsAttr) {
            return settingsAttr.value;
        }
        
        return null;
    }

    getLogoUrl() {
        // Se estiver na rota de login, tenta buscar a logo específica do grupo "Login"
        const currentUrl = (this.router && this.router.url ? this.router.url : "").toLowerCase();
        const isLoginRoute = currentUrl.includes('/login');

        if (isLoginRoute) {
            // 1) Tenta grupo "login" no nível raiz da política
            const loginGroupRoot = this.getSettings("login");
            if (loginGroupRoot && loginGroupRoot.type && loginGroupRoot.type.toLowerCase() === 'group' && loginGroupRoot.attributes) {
                const loginLogoAttr = loginGroupRoot.attributes
                    .find(attr => attr.key && attr.key.toLowerCase() === "logourl");
                if (loginLogoAttr && loginLogoAttr.value) {
                    return loginLogoAttr.value;
                }
            }

            // 2) Fallback: tenta encontrar grupo "Login" dentro de "default"
            const storageAttrs = localStorage.getItem("designAttributes");
            if (storageAttrs) {
                try {
                    const attributes = <AttributeModel[]>JSON.parse(storageAttrs);
                    const defaultAttr = attributes.find(a => a.key && a.key.toLowerCase() === "default");
                    if (defaultAttr && defaultAttr.attributes) {
                        const loginGroupNested = defaultAttr.attributes
                            .find(a => a.key && a.key.toLowerCase() === "login" && a.type && a.type.toLowerCase() === 'group');
                        if (loginGroupNested && loginGroupNested.attributes) {
                            const loginLogoAttrNested = loginGroupNested.attributes
                                .find(attr => attr.key && attr.key.toLowerCase() === "logourl");
                            if (loginLogoAttrNested && loginLogoAttrNested.value) {
                                return loginLogoAttrNested.value;
                            }
                        }
                    }
                } catch {}
            }
        }

        // Fallback: usa o LogoUrl do "default" ou o arquivo local padrão
        const defaultLogoAttr = this.getFromDefault("LogoUrl");
        if (defaultLogoAttr && defaultLogoAttr.value) {
            return defaultLogoAttr.value;
        }

        return "/assets/img/logo.png";
    }

    getFaviconUrl() {
        let logoAttr = this.getFromDefault("FaviconUrl")

        if(logoAttr) {
            return logoAttr.value
        }

        return "favicon.ico"
    }

    loadDesignPolicy(): Observable<AttributeModel> {
        let designPolicyId = this.authService.getDesignPolicy()

        if(designPolicyId) {
            localStorage.setItem("designPolicyId", designPolicyId)

            return this.motorPolicyService
                .getBy(designPolicyId)
                .pipe(
                    map((policy: ProcessModel) => { 
                        localStorage.setItem("designAttributes", JSON.stringify(policy.attributes))
                        this.setColors()
                        this.setFonts()
                        this.setFontSize()
                        return {
                            key: policy.policyName,
                            type: "Object",
                            attributes: policy.attributes,
                            options: []
                        }
                    })
                )
        } else {
            localStorage.removeItem("designPolicyId")
            localStorage.removeItem("designAttributes")
            this.setColors()
            this.setFonts()
            this.setFontSize()
            return new Observable<AttributeModel>((observer) => {
                observer.next()
                observer.complete()
            })
        }
    }

    loadDesignPolicyByDomain(domain: string): Observable<AttributeModel> {
        return this.policyService
            .getDesignByDomain(domain)
            .pipe(
                map((policy: PolicyModel) => { 
                    localStorage.setItem("designPolicyId", policy.id)
                    localStorage.setItem("designAttributes", JSON.stringify(policy.attributes))
                    this.setColors()
                    this.setFonts()
                    this.setFontSize()
                    return {
                        key: policy.policyName,
                        type: "Object",
                        attributes: policy.attributes,
                        options: []
                    }
                })
            )
    }
}